// src/pages/ServicioDetalle.jsx
import React, { useLayoutEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import {
  Compass,
  BarChart2,
  Target,
  Users,
  DollarSign,
  ShieldCheck,
  TrendingUp,
  PieChart,
  Layers,
  Package,
  FileText,
  CheckSquare,
  Award,
  Search,
  ClipboardCheck,
  Shield,
  Activity,
} from "lucide-react";
import { getServicioBySlug } from "../data/servicios";

const ICONS = {
  Compass,
  BarChart2,
  Target,
  Users,
  DollarSign,
  ShieldCheck,
  TrendingUp,
  PieChart,
  Layers,
  Package,
  FileText,
  CheckSquare,
  Award,
  Search,
  ClipboardCheck,
  Shield,
  Activity,
};

export default function ServicioDetalle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const topRef = useRef(null);
  const servicio = getServicioBySlug(slug);

  useLayoutEffect(() => {
    if (!servicio) return;

    const prevTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const prevDescription = metaDescription?.getAttribute("content");

    document.title = `${servicio.title} | Core Solutions`;
    if (metaDescription) {
      metaDescription.setAttribute("content", servicio.hook);
    }

    return () => {
      document.title = prevTitle;
      if (metaDescription && prevDescription) {
        metaDescription.setAttribute("content", prevDescription);
      }
    };
  }, [servicio]);

  useLayoutEffect(() => {
    const prevScrollRestoration =
      "scrollRestoration" in window.history ? window.history.scrollRestoration : null;

    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch (e) {}

    try {
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: "auto", block: "start" });
        if (typeof topRef.current.focus === "function") {
          topRef.current.focus({ preventScroll: true });
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    } catch (e) {}

    return () => {
      try {
        if ("scrollRestoration" in window.history && prevScrollRestoration) {
          window.history.scrollRestoration = prevScrollRestoration;
        }
      } catch (e) {}
    };
  }, [slug]);

  if (!servicio) {
    return <Navigate to="/" replace />;
  }

  const handleGoToSection = (section) => {
    navigate("/", { state: { scrollTo: section } });
  };

  return (
    <motion.div
      ref={topRef}
      tabIndex={-1}
      className="min-h-screen bg-neutral scroll-mt-24 relative"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Hero */}
      <div
        className="relative w-full h-[500px] flex flex-col items-center justify-center text-center px-6"
        style={{
          backgroundImage: `url('${servicio.image}')`,
          backgroundSize: "cover",
          backgroundPosition: "top",
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <motion.div
          className="relative z-10 max-w-4xl flex flex-col items-center gap-4 pt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-4xl md:text-5xl font-medium text-white drop-shadow-lg">
            {servicio.title}
          </h1>
          <p className="text-lg md:text-xl text-white font-semibold drop-shadow-md">
            {servicio.hook}
          </p>
        </motion.div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-28 space-y-20 text-[#120B3E]">
        {/* Descripción */}
        <div className="bg-white rounded-3xl shadow-xl p-10">
          <div className="space-y-8 text-justify text-lg md:text-xl leading-relaxed">
            {servicio.description.map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}

            <p>
              <span className="font-bold text-primary">{servicio.aspectosLabel}</span>
            </p>

            <ul className="list-disc ml-8 space-y-2">
              {servicio.aspectos.map((aspecto, idx) => (
                <li key={idx}>{aspecto}</li>
              ))}
            </ul>

            {servicio.ejemplos && (
              <>
                <p>
                  <span className="font-bold text-primary">{servicio.ejemplosLabel}</span>
                </p>
                <ul className="list-disc ml-8 space-y-2">
                  {servicio.ejemplos.map((ejemplo, idx) => (
                    <li key={idx}>{ejemplo}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Beneficios */}
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-primary text-center">
            BENEFICIOS
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10">
            {servicio.beneficios.map((beneficio, idx) => {
              const Icon = ICONS[beneficio.icon];
              return (
                <motion.div
                  key={idx}
                  className="bg-white flex flex-col md:flex-row items-start gap-6 p-6 md:p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-shadow transform hover:-translate-y-2 cursor-pointer text-[#120B3E]"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.03 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: idx * 0.2 }}
                >
                  <div className="flex-shrink-0 bg-secondary p-4 rounded-full text-white flex items-center justify-center">
                    {Icon && <Icon size={32} />}
                  </div>
                  <div className="flex flex-col justify-center">
                    <h3 className="font-bold text-xl mb-2">{beneficio.title}</h3>
                    <p className="text-justify">{beneficio.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Botón Contáctanos */}
        <div className="flex justify-center mt-16">
          <button
            onClick={() => handleGoToSection("contactoRef")}
            className="w-56 py-4 bg-gradient-to-r from-secondary to-primary text-white font-semibold rounded-2xl shadow-lg
                       hover:scale-105 hover:shadow-2xl hover:brightness-110
                       transition-transform duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-primary/50"
          >
            Tengamos una reunión
          </button>
        </div>
      </div>

      {/* Botón Volver a Servicios */}
      <div className="absolute bottom-6 left-6">
        <button
          onClick={() => handleGoToSection("serviciosRef")}
          className="w-40 py-2 border-2 border-primary text-primary font-medium rounded-xl
                     hover:bg-primary hover:text-white transition-all duration-300"
        >
          Volver a Servicios
        </button>
      </div>
    </motion.div>
  );
}
